import type { GroupClaimLine, GroupClaimSummary, SaleDetail, SaleLookupRow } from '@/types/sale';
import { hasKey, t, type Locale } from '@/lib/i18n';

/**
 * ABT-S22 — helpers for a partner (travel-agent) group booking at the
 * ticketbox. The rules (claim once, refusals, headcount, reprint permission)
 * are the server's; these only decide what the screen offers.
 */

export function isPartnerBooking(detail?: Pick<SaleDetail, 'isPartnerBooking' | 'groupClaim'> | null): boolean {
  return Boolean(detail && (detail.isPartnerBooking || detail.groupClaim?.isPartnerBooking));
}

/** The claim summary, if the server sent one. */
export function claimOf(detail?: SaleDetail | null): GroupClaimSummary | null {
  return detail?.groupClaim ?? null;
}

/** Wristbands that can still be printed or reprinted (a voided one — a cancelled member — cannot). */
export function activeTickets<T extends { status?: string }>(tickets: T[] | undefined | null): T[] {
  return (tickets ?? []).filter((t) => String(t.status ?? 'ACTIVE').toUpperCase() !== 'INACTIVE');
}

/** The main button's label for a partner booking. */
// Decided 1 Oct (signed): the whole group prints at once — no partial claim.
// #1853: `locale` defaults to Indonesian so callers outside the translated
// screens (PrintTicketPage) keep the text they always had.
export function claimButtonLabel(summary: GroupClaimSummary | null, locale: Locale = 'id'): string {
  if (!summary) return t('claim.button.printAll', undefined, locale);
  if (!summary.claimable) return t('claim.button.claimed', undefined, locale);
  return t('claim.button.claim', { count: summary.headcount }, locale);
}

/** The /orders channel chip key: the server's `channel` when set, else posType. */
export function channelKey(row: Pick<SaleLookupRow, 'channel' | 'posType'>): string {
  return String(row.channel ?? row.posType ?? '').toLowerCase();
}

/**
 * AFB-S14 — the claim panel's heading, from the partner the server named
 * (`partnerType` / `partnerSubtype`), mirroring the backend's
 * helper/partnerChannel: an OTA, a travel agent (also anything unknown — what
 * every partner booking showed before), or an Affiliator · Hotel.
 */
export function partnerGroupHeading(
  summary: Pick<GroupClaimSummary, 'partnerType' | 'partnerSubtype'> | null,
  locale: Locale = 'id',
): string {
  const type = String(summary?.partnerType ?? '').toUpperCase();
  const subtype = String(summary?.partnerSubtype ?? '').toLowerCase();
  if (type === 'OTA') return t('claim.heading.ota', undefined, locale);
  if (type === 'AFFILIATE' || type === 'AFFILIATOR') {
    return t(subtype === 'affiliator:hotel' ? 'claim.heading.hotel' : 'claim.heading.affiliator', undefined, locale);
  }
  return t('claim.heading.agent', undefined, locale);
}

/**
 * #1853 — the claim line ("Diklaim 10:42 di ticketbox oleh Budi, 38 dari 40").
 * Indonesian shows the server's own text unchanged; English is rebuilt from
 * the structured fields the server sends alongside it.
 */
export function claimLineText(claim: GroupClaimLine, locale: Locale = 'id'): string {
  if (locale === 'id') return claim.staffTextId ?? claim.textId;
  const time = String(claim.claimedAtLocal ?? '').slice(11, 16) || '—';
  const whereKey = `claim.where.${String(claim.channel ?? '').toUpperCase()}`;
  const where = hasKey(whereKey) ? t(whereKey, undefined, locale) : String(claim.channel ?? '').toLowerCase();
  const vars = { time, where, name: claim.staffName, claimed: claim.headcountClaimed, booked: claim.headcountBooked };
  return t(claim.staffName ? 'claim.lineStaff' : 'claim.line', vars, locale);
}

/**
 * #1853 — a refusal's message. The server words it in Indonesian
 * (GroupClaimService.refusalFor / summary); Indonesian shows it unchanged.
 * English is rebuilt from its `code` plus the booking number / dates read
 * back out of the message; anything unrecognised keeps the server's text.
 */
export function refusalText(
  refusal: { code: string; message: string },
  summary: Pick<GroupClaimSummary, 'groups'> | null,
  locale: Locale = 'id',
): string {
  if (locale === 'id') return refusal.message;
  const msg = refusal.message ?? '';
  const booking = /Pemesanan (\S+) /.exec(msg)?.[1];
  switch (refusal.code) {
    case 'ALREADY_CLAIMED': {
      const first = summary?.groups.find((g) => g.claim)?.claim;
      return first ? t('claim.refusal.alreadyClaimed', { line: claimLineText(first, locale) }, locale) : msg;
    }
    case 'BOOKING_CANCELLED':
      if (booking === 'ini') return t('claim.refusal.cancelledAny', undefined, locale);
      return booking ? t('claim.refusal.cancelled', { booking }, locale) : msg;
    case 'BOOKING_UNPAID':
      if (!booking) return msg;
      return t(/hotel/i.test(msg) ? 'claim.refusal.unpaidGuest' : 'claim.refusal.unpaidAgent', { booking }, locale);
    case 'WRONG_VISIT_DATE': {
      const m = /tanggal (\S+), bukan hari ini \(([^)]+)\)/.exec(msg);
      return booking && m ? t('claim.refusal.wrongDate', { booking, day: m[1], today: m[2] }, locale) : msg;
    }
    default:
      return msg;
  }
}
